import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { Alert, Chip, Link, Menu, MenuItem, Paper, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import DatosBasicosApi from "api/datosbasicosApi";
import EditSalaZoomModal from "./editSalaZoomModal";

const SalasZoomTable = () => {
  const [salas, setSalas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSala, setSelectedSala] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCodigo, setSelectedCodigo] = useState(null);

  const fetchSalas = async () => {
    try {
      const response = await DatosBasicosApi.fetchSalasZoom();
      setSalas(response.data);
      setError(null);
    } catch (error) {
      console.error("Error al obtener las salas Zoom:", error);
      setError(obtenerMensajeError(error, "Error al cargar las Salas Zoom."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalas();
  }, []);

  const handleMenuClick = (event, codigo) => {
    setAnchorEl(event.currentTarget);
    setSelectedCodigo(codigo);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCodigo(null);
  };

  const handleEdit = (sala) => {
    setSelectedSala(sala);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.codigo)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "nombre",
      headerName: "Sala",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre}
        </SuiTypography>
      ),
    },
    {
      field: "id_reunion",
      headerName: "ID Reunión",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.id_reunion}
        </SuiTypography>
      ),
    },
    {
      field: "url",
      headerName: "Enlace",
      flex: 4,
      renderCell: (params) => (
        <Link
          href={params.row.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ wordBreak: "break-all" }}
        >
          {params.row.url}
        </Link>
      ),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.estado ? "Activa" : "Inactiva"}
          color={params.row.estado ? "success" : "default"}
        />
      ),
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Salas Zoom para Audiencias Virtuales</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={2}>
            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedSala(null);
                setEditModalOpen(true);
              }}
            >
              Nueva Sala
            </SuiButton>
          </SuiBox>
        </SuiBox>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={salas}
          columns={columns}
          getRowId={(row) => row.codigo}
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleEdit(salas.find((s) => s.codigo === selectedCodigo))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
      </Menu>

      <EditSalaZoomModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        sala={selectedSala}
        onSuccess={() => {
          fetchSalas();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default SalasZoomTable;
